import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Checkbox,
    FormControlLabel,
    Typography,
    Box,
    Link,
    Stack,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import { LARAVEL_API_PATH, LARAVEL_API_TOKEN } from '../../constants/path';
import { 
    ConsentModalProps, 
    ConsentState, 
    ConsentEvent, 
    ConsentType,
    AvailableConsentTypesResponse,
    ConsentEventsSearchResponse,
    ConsentVersions
} from './types';
import { setConsentVersions, REQUIRED_CONSENT_TYPES } from '../../helpers';
import { useTypedSelector } from '../../hooks/useTypedSelector';

interface ExtendedConsentModalProps extends ConsentModalProps {
    consentTypesInfo?: AvailableConsentTypesResponse | null;
}

const ConsentModal: React.FC<ExtendedConsentModalProps> = ({ 
    open, 
    onClose, 
    patientFio,
    patientEmail,
    consentTypesInfo
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const { patient } = useTypedSelector(state => state.patientState);
    const [loading, setLoading] = useState(false);
    const [consents, setConsents] = useState<ConsentState>({
        data_processing_consent: false,
        data_transfer_consent: false,
        data_processing_policy_consent: false,
    });
    const [typesInfo, setTypesInfo] = useState<AvailableConsentTypesResponse | null>(
        consentTypesInfo || null
    );
    const [signedConsents, setSignedConsents] = useState<Map<string, number>>(new Map());

    // Загрузка подписанных согласий пользователя
    const fetchSignedConsents = useCallback(async () => {
        if (!patient?.id) {
            return;
        }

        try {
            const response = await axios.get<ConsentEventsSearchResponse>(
                `${LARAVEL_API_PATH}consent-download/events/search`,
                {
                    params: {
                        lk_id: patient.id
                    }
                }
            );

            // Создаем карту подписанных согласий: consent_type -> максимальная text_version
            const signedMap = new Map<string, number>();
            if (response.data?.rows) {
                response.data.rows.forEach((row) => {
                    // Преобразуем text_version в число, так как API может возвращать строку
                    const textVersion = typeof row.text_version === 'string' 
                        ? parseInt(row.text_version, 10) 
                        : row.text_version;
                    const currentVersion = signedMap.get(row.consent_type) || 0;
                    if (textVersion > currentVersion) {
                        signedMap.set(row.consent_type, textVersion);
                    }
                });
            }
            setSignedConsents(signedMap);
        } catch (error) {
            console.error('Error fetching signed consents:', error);
        }
    }, [patient?.id]);

    // Какие типы согласий изменились и требуют подтверждения
    const changedConsentTypes: ConsentType[] = React.useMemo(() => {
        if (!typesInfo) return [] as ConsentType[];

        const changed: ConsentType[] = [];
        REQUIRED_CONSENT_TYPES.forEach((consentType) => {
            const serverVersion = typesInfo?.types_info?.[consentType]?.text_version;
            const signedVersion = signedConsents.get(consentType);

            // Если нет подписанного согласия или версия на сервере выше подписанной
            if (!signedVersion || (serverVersion && serverVersion > signedVersion)) {
                changed.push(consentType);
            }
        });
        return changed;
    }, [typesInfo, signedConsents]);

    const fetchConsentTypes = useCallback(async () => {
        try {
            const response = await axios.get<AvailableConsentTypesResponse>(
                `${LARAVEL_API_PATH}consent-download/types/available`
            );
            setTypesInfo(response.data);
        } catch (error) {
            console.error('Error fetching consent types:', error);
            enqueueSnackbar('Ошибка при загрузке типов согласий', { variant: 'error' });
        }
    }, [enqueueSnackbar]);

    // Получаем информацию о типах согласий, если она не была передана
    useEffect(() => {
        if (open && !typesInfo) {
            void fetchConsentTypes();
        }
    }, [open, typesInfo, fetchConsentTypes]);

    // Загружаем подписанные согласия при открытии модального окна
    useEffect(() => {
        if (open) {
            void fetchSignedConsents();
        }
    }, [open, fetchSignedConsents]);

    // Открытие документа в новой вкладке
    const handleDocumentClick = (consentType: ConsentType) => {
        const url = `${LARAVEL_API_PATH}consent-download/${consentType}`;
        window.open(url, '_blank');
    };

    // Изменение состояния чекбокса
    const handleCheckboxChange = (consentType: keyof ConsentState) => (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setConsents({
            ...consents,
            [consentType]: event.target.checked,
        });
    };

    // Проверяем только те чекбоксы, которые отображаются (измененные документы)
    const allConsentsChecked = React.useMemo(() => {
        if (changedConsentTypes.length === 0) return false;
        return changedConsentTypes.every((type) => consents[type as keyof ConsentState]);
    }, [changedConsentTypes, consents]);

    // Получение IP адреса пользователя
    const getUserIP = async (): Promise<string> => {
        try {
            const response = await axios.get('https://api.ipify.org?format=json');
            return response.data.ip;
        } catch (error) {
            console.error('Error fetching IP:', error);
            return '0.0.0.0';
        }
    };

    // Генерация хеша текста согласия
    const generateTextHash = async (consentType: string): Promise<string> => {
        const text = `${consentType}_${new Date().toISOString().split('T')[0]}`;
        const encoder = new TextEncoder();
        const data = encoder.encode(text);
        const hashBuffer = await crypto.subtle.digest('SHA-256', data);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
        return hashHex;
    };

    // Получение session_id
    const getSessionId = async (): Promise<string> => {
        // Проверяем наличие токена в localStorage
        const existingToken = localStorage.getItem('session_token');
        
        if (existingToken) {
            return existingToken;
        }
        
        try {
            // Если токена нет, получаем его с сервера
            const response = await axios.get(`${LARAVEL_API_PATH}api/token`, {
                headers: {
                    'X-API-Token': LARAVEL_API_TOKEN
                }
            });
            
            // Извлекаем токен из ответа
            if (response.data?.success && response.data?.data?.token) {
                const newToken = response.data.data.token;
                // Сохраняем токен в localStorage
                localStorage.setItem('session_token', newToken);
                return newToken;
            } else {
                throw new Error('Invalid token response');
            }
        } catch (error) {
            console.error('Error fetching session token:', error);
            throw error;
        }
    };

    // Отправка согласия
    const sendConsentEvent = async (
        consentType: ConsentType, 
        ip: string, 
        sessionId: string,
        textVersion?: number,
        name?: string,
        email?: string
    ) => {
        const textHash = await generateTextHash(consentType);
        const event: ConsentEvent = {
            session_id: sessionId,
            source: 'website',
            consent_type: consentType,
            action: 'granted',
            consent_text_hash: textHash,
            ip: ip,
            user_agent: navigator.userAgent,
            text_version: textVersion,
            name: name,
            email: email,
        };

        // Устанавливаем lk_id из данных авторизованного пациента
        if (patient?.id) {
            event.lk_id = patient.id;
        }

        await axios.post(`${LARAVEL_API_PATH}api/consent-events`, event, {
            headers: {
                'X-API-Token': LARAVEL_API_TOKEN
            }
        });
    };

    // Обработка отправки формы
    const handleSubmit = async () => {
        if (!allConsentsChecked) {
            enqueueSnackbar('Необходимо отметить все согласия', { variant: 'warning' });
            return;
        }

        setLoading(true);
        try {
            // Получаем IP и session_id
            const ip = await getUserIP();
            const sessionId = await getSessionId();

            // Получаем версии только для измененных документов
            const updatedVersions: ConsentVersions = {};

            const consentPromises = changedConsentTypes.map((consentType) => {
                const typeInfo = typesInfo?.types_info[consentType];
                const version = typeInfo?.text_version;

                if (version) {
                    updatedVersions[consentType as keyof ConsentVersions] = version;
                }

                return sendConsentEvent(consentType, ip, sessionId, version, patientFio, patientEmail);
            });

            await Promise.all(consentPromises);

            // Обновляем локальный кэш версий для обратной совместимости
            // Основная проверка теперь идет через API /search
            setConsentVersions(updatedVersions);

            // Обновляем список подписанных согласий после отправки
            await fetchSignedConsents();

            enqueueSnackbar('Согласия успешно отправлены', { variant: 'success' });
            onClose();
        } catch (error: any) {
            console.error('Error sending consents:', error);
            enqueueSnackbar(
                error?.response?.data?.message || 'Ошибка при отправке согласий',
                { variant: 'error' }
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            maxWidth="md"
            fullWidth
            disableEscapeKeyDown
            onClose={(event, reason) => {
                // Блокируем закрытие модального окна по клику вне его или по Escape
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                    return;
                }
            }}
        >
            <DialogTitle
                sx={{
                    pb: { xs: 1, sm: 1.5 },
                    pt: { xs: 2, sm: 3 },
                    px: { xs: 2, sm: 3 },
                }}
            >
                <Typography 
                    variant="h6" 
                    component="div"
                    sx={{
                        fontSize: { xs: '0.875rem', sm: '1rem', md: '1.25rem' },
                        lineHeight: { xs: 1.4, sm: 1.5 },
                    }}
                >
                    Для оказания услуг ООО "Доктор АРБИТАЙЛО" просьба подписать Согласие на обработку персональных данных и Согласие на передачу персональных данных, а также ознакомиться с условиями обработки персональных данных, указанными в Политике обработки персональных данных.
                </Typography>
            </DialogTitle>
            <DialogContent
                sx={{
                    maxHeight: { xs: 'calc(100vh - 200px)', sm: 'none' },
                    overflow: 'auto',
                    px: { xs: 2, sm: 3 },
                }}
            >
                <Stack spacing={2}>
                    {changedConsentTypes.includes('data_processing_consent') && (
                    <Box>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={consents.data_processing_consent}
                                    onChange={handleCheckboxChange('data_processing_consent')}
                                    sx={{ '&.Mui-checked': { color: '#d23731' } }}
                                />
                            }
                            label={
                                <Typography
                                    sx={{
                                        fontSize: { xs: '0.750rem', sm: '0.875rem', md: '1rem' },
                                    }}
                                >
                                    Я, {patientFio}, даю свое{' '}
                                    <Link
                                        component="button"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleDocumentClick('data_processing_consent');
                                        }}
                                        sx={{ cursor: 'pointer', textDecoration: 'underline', display: 'inline' }}
                                    >
                                        Согласие на обработку персональных данных
                                    </Link>
                                </Typography>
                            }
                        />
                    </Box>
                    )}
                    {changedConsentTypes.includes('data_transfer_consent') && (
                    <Box>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={consents.data_transfer_consent}
                                    onChange={handleCheckboxChange('data_transfer_consent')}
                                    sx={{ '&.Mui-checked': { color: '#d23731' } }}
                                />
                            }
                            label={
                                <Typography
                                    sx={{
                                        fontSize: { xs: '0.750rem', sm: '0.875rem', md: '1rem' },
                                    }}
                                >
                                    Я, {patientFio}, даю свое{' '}
                                    <Link
                                        component="button"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleDocumentClick('data_transfer_consent');
                                        }}
                                        sx={{ cursor: 'pointer', textDecoration: 'underline', display: 'inline' }}
                                    >
                                        Согласие на передачу персональных данных
                                    </Link>
                                </Typography>
                            }
                        />
                    </Box>
                    )}
                    {changedConsentTypes.includes('data_processing_policy_consent') && (
                    <Box>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={consents.data_processing_policy_consent}
                                    onChange={handleCheckboxChange('data_processing_policy_consent')}
                                    sx={{ '&.Mui-checked': { color: '#d23731' } }}
                                />
                            }
                            label={
                                <Typography
                                    sx={{
                                        fontSize: { xs: '0.750rem', sm: '0.875rem', md: '1rem' },
                                    }}
                                >
                                    Я, {patientFio}, подтверждаю ознакомление с{' '}
                                    <Link
                                        component="button"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleDocumentClick('data_processing_policy_consent');
                                        }}
                                        sx={{ cursor: 'pointer', textDecoration: 'underline', display: 'inline' }}
                                    >
                                        Политикой обработки персональных данных
                                    </Link>
                                </Typography>
                            }
                        />
                    </Box>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, px: { xs: 2, sm: 3 } }}>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    color="secondary"
                    disabled={!allConsentsChecked || loading}
                    fullWidth
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                >
                    {loading ? 'Отправка...' : 'Отправить'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ConsentModal;

